import React, { useState, useEffect, useCallback } from 'react';
import { X, MessageSquare, Clock, User, Tag, AlertCircle, CheckCircle, Edit3 } from 'lucide-react';
import {
  TICKET_STATUSES,
  TICKET_PRIORITIES,
  STATUS_COLORS,
  PRIORITY_COLORS
} from '../../../../constant/constant';
import { useSupportTickets } from '../../../../hooks/useSupportTickets';

const TicketDetailModal = ({
  isOpen,
  onClose,
  ticketId,
  onUpdateTicket,
  onRespondToTicket
}) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [responding, setResponding] = useState(false);
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const {
    fetchTicketDetails,
    respondToTicket: respondToTicketAction,
    updateTicketStatus: updateTicketStatusAction
  } = useSupportTickets();

  const normalizeTicketData = (data) => {
    if (!data) return null;
    if (data.ticket) return data.ticket;
    return data;
  };

  const loadTicketDetails = useCallback(async () => {
    if (!ticketId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchTicketDetails(ticketId);
      setTicket(normalizeTicketData(data));
    } catch (err) {
      setError(err.message || 'Failed to load ticket details');
      setTicket(null);
    } finally {
      setLoading(false);
    }
  }, [fetchTicketDetails, ticketId]);

  useEffect(() => {
    if (isOpen && ticketId) {
      loadTicketDetails();
    } else if (!isOpen) {
      setTicket(null);
      setResponseMessage('');
      setIsInternal(false);
      setShowStatusUpdate(false);
      setNewStatus('');
      setError(null);
    }
  }, [isOpen, ticketId, loadTicketDetails]);

  const handleRespond = async () => {
    if (!responseMessage.trim()) return;

    setResponding(true);
    try {
      await respondToTicketAction(ticketId, responseMessage, isInternal);
      await loadTicketDetails();
      setResponseMessage('');
      setIsInternal(false);
      if (onRespondToTicket) {
        onRespondToTicket(ticketId, responseMessage, isInternal);
      }
    } catch (err) {
      setError(err.message || 'Failed to send response');
    } finally {
      setResponding(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;

    try {
      await updateTicketStatusAction(ticketId, newStatus);
      await loadTicketDetails();
      setShowStatusUpdate(false);
      setNewStatus('');

      if (onUpdateTicket) {
        onUpdateTicket(ticketId, { status: newStatus });
      }
    } catch (err) {
      setError(err.message || 'Failed to update ticket status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header - Fixed */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Ticket #{ticket?.ticketId}
            </h2>
            {ticket && (
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-800'}`}>
                  {ticket.status}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[ticket.priority] || 'bg-gray-100 text-gray-800'}`}>
                  {ticket.priority}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="p-6 text-center flex-1 flex items-center justify-center">
            <div>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading ticket details...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-6 text-center flex-1 flex items-center justify-center">
            <div>
              <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <p className="text-red-600">{error}</p>
            </div>
          </div>
        ) : ticket ? (
          <>
            {/* Scrollable Content Area */}
            <div className="flex-1 overflow-y-auto min-h-0 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
              {/* Ticket Info */}
              <div className="p-6 border-b border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{ticket.subject}</h3>
                    <p className="text-gray-600 text-sm mb-4">{ticket.description}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center text-sm">
                      <User className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">User:</span>
                      <span className="ml-2 font-medium">{ticket?.userId?.firstName} {ticket?.userId?.lastName}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Tag className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">Category:</span>
                      <span className="ml-2 font-medium">{ticket.category}</span>
                    </div>
                    <div className="flex items-center text-sm">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-gray-600">Created:</span>
                      <span className="ml-2 font-medium">{formatDate(ticket.createdAt)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Responses */}
              <div className="p-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Conversation</h4>
                <div className="space-y-4">
                  {ticket.responses && ticket.responses.length > 0 ? (
                    ticket.responses.map((response) => (
                      <div key={response.id || response._id} className={`p-4 rounded-lg ${response.isInternal ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900">{response.userName || response.user?.name || 'Admin'}</span>
                            {response.isInternal && (
                              <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                                Internal Note
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(response.createdAt)}</span>
                        </div>
                        <p className="text-gray-700">{response.message}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-sm">No responses yet.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Response Form - Fixed at Bottom */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="internal"
                    checked={isInternal}
                    onChange={(e) => setIsInternal(e.target.checked)}
                    className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                  />
                  <label htmlFor="internal" className="text-sm text-gray-600">
                    Internal note (not visible to user)
                  </label>
                </div>

                <textarea
                  value={responseMessage}
                  onChange={(e) => setResponseMessage(e.target.value)}
                  placeholder="Type your response..."
                  className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  rows={3}
                />

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowStatusUpdate(!showStatusUpdate)}
                      className="flex items-center space-x-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      <Edit3 className="w-4 h-4" />
                      <span>Update Status</span>
                    </button>
                  </div>

                  <div className="flex items-center space-x-2">
                    {showStatusUpdate && (
                      <select
                        value={newStatus}
                        onChange={(e) => setNewStatus(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                      >
                        <option value="">Select Status</option>
                        {Object.values(TICKET_STATUSES).map(status => (
                          <option key={status} value={status}>{status}</option>
                        ))}
                      </select>
                    )}

                    {showStatusUpdate && newStatus && (
                      <button
                        onClick={handleStatusUpdate}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
                      >
                        Update
                      </button>
                    )}

                    <button
                      onClick={handleRespond}
                      disabled={!responseMessage.trim() || responding}
                      className="flex items-center space-x-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>{responding ? 'Sending...' : 'Send Response'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default TicketDetailModal;
